/**
 * Platform compliance desk and the slim outbound-status banner.
 * Props match the other admin modules: api is API.json (method, path, body),
 * toast(message, type), user, timezone. ComplianceAdminView also takes onOpenTenant(id).
 * ComplianceStatusBanner({ api, user }) refreshes every 5 minutes.
 */
import { formatDateTime } from './datetime.js';
import { ReassignedSettingsCard } from './reassigned-settings.js';

const { useState, useEffect, useCallback } = React;

export const BLOCK_REASON_LABELS = {
  platform_paused: 'Paused by Nightcoders',
  tenant_suspended: 'Account suspended',
  terms_not_accepted: 'Terms not accepted',
  opted_out: 'Opted out / do not call',
  outside_calling_hours: 'Outside calling hours',
  frequency_cap: 'Too many attempts in 24h',
  no_consent: 'No consent on file',
  not_business_landline: 'Not a business landline',
  line_type_unknown: 'Line type unknown'
};

const DOC_LABELS = {
  terms: 'Terms of Service',
  aup: 'Acceptable Use',
  outbound_compliance: 'Outbound compliance'
};

const ALERT_LABELS = {
  opt_out_rate: 'Opt-out rate',
  blocked_dials: 'Blocked dials'
};

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function reasonLabel(code) {
  if (BLOCK_REASON_LABELS[code]) return BLOCK_REASON_LABELS[code];
  const text = String(code || '').replace(/_/g, ' ').trim();
  if (!text) return 'Other';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatRate(rate, answered) {
  if (!answered) return '\u2014';
  const pct = Math.round(Number(rate) * 1000) / 10;
  if (!Number.isFinite(pct)) return '\u2014';
  if (Math.abs(pct - Math.round(pct)) < 1e-9) return Math.round(pct) + '%';
  return pct.toFixed(1) + '%';
}

function percentField(threshold) {
  const pct = Math.round(Number(threshold) * 1000) / 10;
  if (!Number.isFinite(pct)) return '5';
  return String(pct);
}

function when(value, timezone) {
  if (!value) return '';
  return formatDateTime(value, { timeZone: timezone, style: 'full' });
}

function creditStatus(platform, timezone) {
  if (!platform || platform.dnc_credits_remaining == null) return 'No lookups yet';
  const remaining = Number(platform.dnc_credits_remaining).toLocaleString('en-US');
  const seen = when(platform.dnc_credits_checked_at, timezone);
  if (!seen) return remaining + ' credits remaining.';
  return remaining + ' credits remaining. Last seen ' + seen + '.';
}

function allowanceUsage(allowance) {
  const used = Number(allowance && allowance.scrubs_used) || 0;
  if (allowance && allowance.scrub_limit == null) return 'Used ' + used + ' (no limit)';
  const limit = allowance && allowance.scrub_limit != null ? Number(allowance.scrub_limit) : 0;
  const remaining = allowance && allowance.remaining != null
    ? Number(allowance.remaining)
    : Math.max(0, limit - used);
  return 'Used ' + used + ' of ' + limit + ' · ' + remaining + ' left';
}

function DncAllowanceEditor({ tenant, api, disabled, onSaved }) {
  const allowance = tenant.allowance || { mode: 'off', scrub_limit: 0, scrubs_used: 0, remaining: 0 };
  const savedMode = allowance.mode || 'off';
  const savedLimit = allowance.scrub_limit;
  const savedUsed = allowance.scrubs_used;
  const [mode, setMode] = useState(savedMode);
  const [noLimit, setNoLimit] = useState(savedLimit == null);
  const [limit, setLimit] = useState(savedLimit == null ? '' : String(savedLimit));
  const [saving, setSaving] = useState(false);
  const [rowError, setRowError] = useState('');

  useEffect(() => {
    setMode(savedMode);
    setNoLimit(savedLimit == null);
    setLimit(savedLimit == null ? '' : String(savedLimit));
  }, [tenant.id, savedMode, savedLimit, savedUsed]);

  async function save(event) {
    event.preventDefault();
    setRowError('');
    let scrubLimit = null;
    if (!noLimit) {
      if (!/^\d+$/.test(String(limit).trim())) {
        setRowError('Enter a whole number, or choose No limit.');
        return;
      }
      scrubLimit = Number(String(limit).trim());
    }
    setSaving(true);
    try {
      const data = await api('PUT', '/api/admin/compliance/tenants/' + tenant.id + '/dnc-allowance', {
        mode,
        scrub_limit: scrubLimit
      });
      if (onSaved) onSaved(tenant.id, data && data.allowance);
    } catch (err) {
      setRowError(errorMessage(err, 'Could not save Do Not Call checks.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className="ca-allowance">
      <label className="ca-label">
        Mode
        <select className="ca-select" value={mode} disabled={disabled || saving} onChange={(event) => setMode(event.target.value)}>
          <option value="off">Off</option>
          <option value="auto">Check every call</option>
          <option value="manual">Manual check only</option>
        </select>
      </label>
      <label className="ca-label">
        Check limit
        <input
          className="ca-input"
          type="number"
          min="0"
          step="1"
          value={noLimit ? '' : limit}
          disabled={disabled || saving || noLimit}
          onChange={(event) => setLimit(event.target.value)}
        />
      </label>
      <label className="ca-check">
        <input
          type="checkbox"
          checked={noLimit}
          disabled={disabled || saving}
          onChange={(event) => {
            const checked = event.target.checked;
            setNoLimit(checked);
            if (!checked && String(limit).trim() === '') setLimit('0');
          }}
        />
        No limit
      </label>
      <p className="ca-help">{allowanceUsage(allowance)}</p>
      {rowError ? <p className="ca-help" role="alert">{rowError}</p> : null}
      <button type="submit" className="ca-btn" disabled={disabled || saving}>{saving ? 'Saving…' : 'Save checks'}</button>
    </form>
  );
}

function isSuper(user) {
  return !!(user && (user.role === 'super_admin' || user.role === 'superadmin'));
}

function suspendedBanner(reason) {
  const why = reason && String(reason).trim();
  if (!why) return 'Outbound calling is suspended for your account. Contact support.';
  const sentence = /[.!?]$/.test(why) ? why : why + '.';
  return 'Outbound calling is suspended for your account. ' + sentence + ' Contact support.';
}

function alertSummary(alert) {
  const details = alert && alert.details;
  if (!details) return '';
  if (alert.kind === 'opt_out_rate') {
    const rate = formatRate(details.rate, details.answeredCalls);
    return details.optOuts + ' opt-outs of ' + details.answeredCalls + ' answered calls (' + rate + ')';
  }
  if (alert.kind === 'blocked_dials') {
    return details.blocked + ' blocked dials in 24 hours';
  }
  return '';
}

export function ComplianceStatusBanner({ api, user }) {
  const [status, setStatus] = useState(null);
  const userId = user && user.id;
  const tenantId = user && user.tenant_id;
  const role = user && user.role;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await api('GET', '/api/compliance/status');
        if (!cancelled) setStatus(data || null);
      } catch (err) {
        // Keep the last banner. A failed refresh should not clear a real pause.
      }
    }
    load();
    const timer = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api, userId, tenantId, role]);

  if (!status || (!status.platform_paused && !status.tenant_suspended)) return null;
  return (
    <div className="ca-banners">
      {status.platform_paused ? (
        <div className="ca-banner" role="status">Outbound calling is temporarily paused by Nightcoders.</div>
      ) : null}
      {status.tenant_suspended ? (
        <div className="ca-banner" role="status">{suspendedBanner(status.suspended_reason)}</div>
      ) : null}
    </div>
  );
}

export function ComplianceAdminView({ api, toast, user, timezone, onOpenTenant }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [confirmPause, setConfirmPause] = useState(false);
  const [confirmResume, setConfirmResume] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);
  const [thresholdPct, setThresholdPct] = useState('5');
  const [minCalls, setMinCalls] = useState('50');
  const [autoSuspend, setAutoSuspend] = useState(true);
  const [provider, setProvider] = useState('none');
  const [enforcement, setEnforcement] = useState('off');
  const [suspendFor, setSuspendFor] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [resumeFor, setResumeFor] = useState(null);
  const zone = timezone || 'America/Los_Angeles';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api('GET', '/api/admin/compliance/overview');
      setOverview(data);
      const platform = data && data.platform;
      if (platform) {
        setThresholdPct(percentField(platform.opt_out_rate_threshold));
        setMinCalls(String(platform.opt_out_min_calls));
        setAutoSuspend(!!platform.auto_suspend);
        setProvider(platform.dnc_provider || 'none');
        setEnforcement(platform.dnc_enforcement || 'off');
      }
    } catch (err) {
      setError(errorMessage(err, 'Could not load compliance.'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  function notify(message, type) {
    if (toast) toast(message, type);
  }

  function applyAllowance(tenantId, allowance) {
    setOverview((current) => {
      if (!current || !Array.isArray(current.tenants)) return current;
      return {
        ...current,
        tenants: current.tenants.map((row) => (
          row.id === tenantId ? { ...row, allowance: allowance || row.allowance } : row
        ))
      };
    });
    notify('Do Not Call checks saved.');
  }

  async function runAction(work, success) {
    setBusy(true);
    try {
      await work();
      if (success) notify(success);
      await load();
    } catch (err) {
      notify(errorMessage(err, 'Could not save that change.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function pause(event) {
    event.preventDefault();
    const reason = pauseReason.trim();
    if (!reason) {
      notify('Enter a reason before pausing.', 'error');
      return;
    }
    if (!confirmPause) {
      setConfirmPause(true);
      return;
    }
    await runAction(
      () => api('POST', '/api/admin/compliance/pause', { reason }),
      'Outbound calling is paused for every workspace.'
    );
    setPauseReason('');
    setConfirmPause(false);
  }

  async function resume() {
    if (!confirmResume) {
      setConfirmResume(true);
      return;
    }
    await runAction(
      () => api('POST', '/api/admin/compliance/resume'),
      'Outbound calling has resumed.'
    );
    setConfirmResume(false);
  }

  async function saveSettings(event) {
    event.preventDefault();
    const threshold = Number(thresholdPct) / 100;
    const minimum = Number(minCalls);
    await runAction(
      () => api('PUT', '/api/admin/compliance/settings', {
        opt_out_rate_threshold: threshold,
        opt_out_min_calls: minimum,
        auto_suspend: autoSuspend,
        dnc_provider: provider,
        dnc_enforcement: enforcement
      }),
      'Compliance settings saved.'
    );
  }

  async function suspend(event) {
    event.preventDefault();
    const reason = suspendReason.trim();
    if (!suspendFor || !reason) {
      notify('Enter a reason before suspending.', 'error');
      return;
    }
    const id = suspendFor.id;
    await runAction(
      () => api('POST', '/api/admin/tenants/' + id + '/outbound-suspend', { reason }),
      'Outbound calling is suspended for ' + (suspendFor.name || 'that workspace') + '.'
    );
    setSuspendFor(null);
    setSuspendReason('');
  }

  async function resumeTenant() {
    if (!resumeFor) return;
    const id = resumeFor.id;
    const name = resumeFor.name;
    await runAction(
      () => api('POST', '/api/admin/tenants/' + id + '/outbound-resume'),
      'Outbound calling resumed for ' + (name || 'that workspace') + '.'
    );
    setResumeFor(null);
  }

  async function resolveAlert(alert) {
    await runAction(
      () => api('POST', '/api/admin/compliance/alerts/' + alert.id + '/resolve'),
      'Alert resolved.'
    );
  }

  async function runCheck() {
    if (!confirmRun) {
      setConfirmRun(true);
      return;
    }
    setConfirmRun(false);
    setBusy(true);
    try {
      const summary = await api('POST', '/api/admin/compliance/run-monitor');
      const checked = summary && summary.checked != null ? summary.checked : 0;
      const alerted = summary && summary.alerted != null ? summary.alerted : 0;
      const suspended = summary && summary.suspended != null ? summary.suspended : 0;
      notify('Checked ' + checked + ' workspaces. New alerts: ' + alerted + '. Suspended: ' + suspended + '.');
      await load();
    } catch (err) {
      notify(errorMessage(err, 'Could not run the check.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  if (!isSuper(user)) {
    return (
      <section className="ca">
        <h1 className="ca-title">Calling compliance</h1>
        <p className="ca-error">Only a platform admin can open this page.</p>
      </section>
    );
  }

  const platform = overview && overview.platform;
  const tenants = (overview && overview.tenants) || [];
  const alerts = (overview && overview.alerts) || [];
  const threshold = platform ? Number(platform.opt_out_rate_threshold) : 0.05;
  const paused = !!(platform && platform.outbound_paused);
  const sorted = tenants.slice().sort((a, b) => {
    if (a.outbound_suspended !== b.outbound_suspended) return a.outbound_suspended ? -1 : 1;
    if (Number(b.opt_out_rate_7d) !== Number(a.opt_out_rate_7d)) return Number(b.opt_out_rate_7d) - Number(a.opt_out_rate_7d);
    return String(a.name || '').localeCompare(String(b.name || ''));
  });

  return (
    <section className="ca">
      <header className="ca-header">
        <div>
          <h1 className="ca-title">Calling compliance</h1>
          <p className="ca-lead">Pause the platform, suspend a workspace, and watch opt-out rates.</p>
        </div>
        <div className="ca-actions">
          <button type="button" className="ca-btn" disabled={busy} onClick={() => load()}>Refresh</button>
          <button type="button" className="ca-btn ca-btn--primary" disabled={busy} onClick={runCheck}>Run check now</button>
        </div>
      </header>

      {confirmRun ? (
        <div className="ca-confirm">
          <p className="ca-help">Run the opt-out check now? A workspace over the threshold can be suspended automatically.</p>
          <div className="ca-actions">
            <button type="button" className="ca-btn ca-btn--primary" disabled={busy} onClick={runCheck}>Run check</button>
            <button type="button" className="ca-btn" disabled={busy} onClick={() => setConfirmRun(false)}>Cancel</button>
          </div>
        </div>
      ) : null}

      {error ? <p className="ca-error">{error}</p> : null}
      {loading && !overview ? <p className="ca-help">Loading compliance…</p> : null}

      <section className={'ca-card' + (paused ? ' ca-kill--paused' : '')}>
        <div className="ca-card-head">
          <h2>Platform kill switch</h2>
          {paused ? <span className="ca-badge">Paused</span> : null}
        </div>
        {paused ? (
          <div>
            <p className="ca-meta">{platform.paused_reason}</p>
            <p className="ca-help">
              {'Paused by ' + (platform.paused_by_name || 'a platform admin')}
              {platform.paused_at ? ' · ' + when(platform.paused_at, zone) : ''}
            </p>
            {confirmResume ? (
              <div className="ca-confirm">
                <p className="ca-help">Resume AI and outbound calling for every workspace?</p>
                <div className="ca-actions">
                  <button type="button" className="ca-btn ca-btn--primary" disabled={busy} onClick={resume}>Resume</button>
                  <button type="button" className="ca-btn" disabled={busy} onClick={() => setConfirmResume(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <button type="button" className="ca-btn ca-btn--primary" disabled={busy} onClick={resume}>Resume</button>
            )}
          </div>
        ) : (
          <form onSubmit={pause}>
            <label className="ca-label">
              Reason
              <textarea className="ca-note" maxLength={300} value={pauseReason} disabled={busy} onChange={(event) => { setPauseReason(event.target.value); setConfirmPause(false); }} required />
            </label>
            {confirmPause ? (
              <div className="ca-confirm">
                <p className="ca-help">This stops AI and outbound calling for every workspace.</p>
                <div className="ca-actions">
                  <button type="submit" className="ca-btn ca-btn--danger" disabled={busy}>Confirm pause</button>
                  <button type="button" className="ca-btn" disabled={busy} onClick={() => setConfirmPause(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <button type="submit" className="ca-btn ca-btn--danger" disabled={busy || !pauseReason.trim()}>Pause all AI and outbound calling</button>
            )}
          </form>
        )}
      </section>

      <form className="ca-card" onSubmit={saveSettings}>
        <h2>Opt-out monitor</h2>
        <p className="ca-help">A workspace at or above the minimum answered calls is suspended when its 7-day opt-out rate is over the threshold. Suspensions stay until you resume them.</p>
        <div className="ca-form-row">
          <label className="ca-label">
            Opt-out threshold (%)
            <input className="ca-input" type="number" min="0.5" max="50" step="0.1" value={thresholdPct} disabled={busy} onChange={(event) => setThresholdPct(event.target.value)} />
          </label>
          <label className="ca-label">
            Minimum answered calls
            <input className="ca-input" type="number" min="10" max="10000" step="1" value={minCalls} disabled={busy} onChange={(event) => setMinCalls(event.target.value)} />
          </label>
          <label className="ca-check">
            <input type="checkbox" checked={autoSuspend} disabled={busy} onChange={(event) => setAutoSuspend(event.target.checked)} />
            Auto-suspend
          </label>
          <label className="ca-label">
            National DNC provider
            <select className="ca-select" value={provider} disabled={busy} onChange={(event) => setProvider(event.target.value)}>
              <option value="none">None</option>
              <option value="registry_file">National registry file</option>
              <option value="dncscrub">DNCScrub (live National and State lists)</option>
              <option value="donotcalldnc">DoNotCallDNC (National list, prepaid credits)</option>
            </select>
          </label>
          <label className="ca-label">
            Do Not Call enforcement
            <select className="ca-select" value={enforcement} disabled={busy} onChange={(event) => setEnforcement(event.target.value)}>
              <option value="off">Off</option>
              <option value="warn">Warn</option>
              <option value="enforce">Enforce</option>
            </select>
          </label>
        </div>
        <p className="ca-help">National registry file uses each workspace&apos;s uploaded national do-not-call file. A file older than 31 days is stale. DNCScrub checks the National and State registries with each workspace&apos;s own FTC SAN.</p>
        {enforcement === 'enforce' ? (
          <p className="ca-help">Enforce blocks sales calls that hit a Do Not Call list. Written consent still allows the call, except to a known TCPA litigator.</p>
        ) : null}
        {provider !== 'donotcalldnc' ? (
          <p className="ca-help">{platform && platform.dncscrub_configured ? 'DNCScrub API key is configured.' : 'DNCScrub API key is not configured. Workspaces can save a SAN, but live checks stay off.'}</p>
        ) : null}
        {provider === 'donotcalldnc' ? (
          <>
            <p className="ca-help">{platform && platform.donotcalldnc_configured ? 'DoNotCallDNC API key is configured.' : 'DoNotCallDNC API key is not configured. Live checks stay off until the key is set.'}</p>
            <p className="ca-help">{creditStatus(platform, zone)}</p>
            {platform && platform.dnc_credits_remaining != null && Number(platform.dnc_credits_remaining) < 1000 ? (
              <p className="ca-help">Credits are low. Buy more at donotcalldnc.com before calls start failing.</p>
            ) : null}
            {platform && platform.dnc_coverage && platform.dnc_coverage.state === false ? (
              <p className="ca-help">Checks the National Do Not Call Registry only. State lists and known litigators are not included. Assign each workspace a mode and how many checks it can use in the table below. Those checks use the platform credits.</p>
            ) : null}
          </>
        ) : null}
        {platform && platform.has_dnc_config ? <p className="ca-help">A provider credential is stored on the server.</p> : null}
        <button type="submit" className="ca-btn ca-btn--primary" disabled={busy}>Save settings</button>
      </form>

      {suspendFor ? (
        <form className="ca-card" onSubmit={suspend}>
          <h2>Suspend {suspendFor.name}</h2>
          <p className="ca-help">Outbound calling stays blocked until you resume this workspace.</p>
          <label className="ca-label">
            Reason
            <textarea className="ca-note" maxLength={300} value={suspendReason} disabled={busy} onChange={(event) => setSuspendReason(event.target.value)} required />
          </label>
          <div className="ca-actions">
            <button type="submit" className="ca-btn ca-btn--danger" disabled={busy || !suspendReason.trim()}>Suspend</button>
            <button type="button" className="ca-btn" disabled={busy} onClick={() => { setSuspendFor(null); setSuspendReason(''); }}>Cancel</button>
          </div>
        </form>
      ) : null}

      {resumeFor ? (
        <div className="ca-card">
          <h2>Resume {resumeFor.name}</h2>
          <p className="ca-help">Allow outbound calling for this workspace again?</p>
          <div className="ca-actions">
            <button type="button" className="ca-btn ca-btn--primary" disabled={busy} onClick={resumeTenant}>Resume</button>
            <button type="button" className="ca-btn" disabled={busy} onClick={() => setResumeFor(null)}>Cancel</button>
          </div>
        </div>
      ) : null}

      <ReassignedSettingsCard api={api} toast={toast} />

      <section className="ca-card">
        <h2>Workspaces</h2>
        <div className="ca-scroll">
          <table className="ca-table">
            <caption className="ca-sr">Workspaces, 7-day opt-out rates, suspension, and Do Not Call checks</caption>
            <thead>
              <tr>
                <th>Workspace</th>
                <th>Answered (7d)</th>
                <th>Opt-outs (7d)</th>
                <th>Opt-out rate</th>
                <th>Blocked (24h)</th>
                <th>Top block reasons (7d)</th>
                <th>Terms</th>
                <th>Do Not Call checks</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((tenant) => {
                const over = Number(tenant.answered_calls_7d) > 0 && Number(tenant.opt_out_rate_7d) > threshold;
                const missing = tenant.terms_missing || [];
                const reasons = tenant.top_block_reasons || [];
                return (
                  <tr key={tenant.id}>
                    <td>
                      {onOpenTenant ? (
                        <button type="button" className="ca-link" onClick={() => onOpenTenant(tenant.id)}>{tenant.name}</button>
                      ) : tenant.name}
                    </td>
                    <td>{tenant.answered_calls_7d}</td>
                    <td>{tenant.opt_outs_7d}</td>
                    <td>
                      <span className={over ? 'ca-rate--over' : ''} title={over ? 'Over the ' + formatRate(threshold, 1) + ' threshold' : undefined}>
                        {formatRate(tenant.opt_out_rate_7d, tenant.answered_calls_7d)}
                      </span>
                    </td>
                    <td>{tenant.blocked_dials_24h}</td>
                    <td>
                      {reasons.length ? (
                        <ul className="ca-reasons">
                          {reasons.map((item) => (
                            <li key={item.code}>{reasonLabel(item.code)} ({item.count})</li>
                          ))}
                        </ul>
                      ) : '\u2014'}
                    </td>
                    <td>
                      {missing.length ? (
                        <span className="ca-pill ca-pill--warn">Missing {missing.map((key) => DOC_LABELS[key] || key).join(', ')}</span>
                      ) : (
                        <span className="ca-pill ca-pill--ok">Accepted</span>
                      )}
                    </td>
                    <td>
                      <DncAllowanceEditor tenant={tenant} api={api} disabled={busy} onSaved={applyAllowance} />
                    </td>
                    <td>
                      {tenant.outbound_suspended ? (
                        <div className="ca-actions">
                          <span className="ca-badge">Suspended</span>
                          <button type="button" className="ca-btn" disabled={busy} onClick={() => { setResumeFor(tenant); setSuspendFor(null); }}>Resume</button>
                        </div>
                      ) : (
                        <button type="button" className="ca-btn" disabled={busy} onClick={() => { setSuspendFor(tenant); setSuspendReason(''); setResumeFor(null); }}>Suspend</button>
                      )}
                      {tenant.outbound_suspended && tenant.outbound_suspended_reason ? (
                        <p className="ca-help">{tenant.outbound_suspended_reason}</p>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && !sorted.length ? <p className="ca-help">No workspaces yet.</p> : null}
      </section>

      <section className="ca-card">
        <h2>Open alerts</h2>
        {alerts.length ? (
          <ul className="ca-alert-list">
            {alerts.map((alert) => (
              <li key={alert.id} className={'ca-alert ca-alert--' + (alert.severity === 'critical' ? 'critical' : 'warning')}>
                <div>
                  <strong>{ALERT_LABELS[alert.kind] || alert.kind}</strong>
                  {' '}
                  <span className={'ca-pill ' + (alert.severity === 'critical' ? 'ca-pill--critical' : 'ca-pill--warn')}>{alert.severity}</span>
                  <p className="ca-meta">
                    {alert.tenant_name || (alert.tenant_id ? 'Workspace ' + alert.tenant_id : 'Platform')}
                    {alert.created_at ? ' · ' + when(alert.created_at, zone) : ''}
                  </p>
                  {alertSummary(alert) ? <p className="ca-help">{alertSummary(alert)}</p> : null}
                </div>
                <button type="button" className="ca-btn" disabled={busy} onClick={() => resolveAlert(alert)}>Resolve</button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ca-help">{loading ? 'Loading alerts…' : 'No open alerts.'}</p>
        )}
      </section>
    </section>
  );
}
